// Workaround for Parcel scopeHoist: false - use namespace import
import * as ReactQuery from "@tanstack/react-query";
import { QueryCache, MutationCache } from "@tanstack/query-core";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
  get as getItem,
  set as setItem,
  del as removeItem,
  entries,
} from "idb-keyval";

import { store } from "./index";
import { getRetryAfterMs } from "@app/utils/requestQueue";

/**
 * How many times a rate limited (`429`) request is retried by react-query.
 * Each attempt waits for the server requested `Retry-After` window and is
 * queued by the service layer, so it never bursts against the endpoint.
 */
const MAX_RATE_LIMIT_RETRIES = 5;

const delayWithJitter = (baseDelay, jitter = 0.5) => {
  const jitterValue = baseDelay * jitter * Math.random();
  const shouldAddJitter = Math.random() < 0.5;
  return Math.floor(
    shouldAddJitter
      ? baseDelay + jitterValue
      : Math.max(0, baseDelay - jitterValue),
  );
};

// A server requested cool-down is a minimum: only ever wait longer than asked,
// otherwise the retry is guaranteed to be rate limited again.
const delayWithUpwardJitter = (baseDelay, jitter = 0.5) =>
  Math.floor(baseDelay + baseDelay * jitter * Math.random());

// Extract QueryClient from namespace import (works better with Parcel scopeHoist: false)
const QueryClient = ReactQuery.QueryClient;

if (!QueryClient || typeof QueryClient !== "function") {
  throw new Error(
    "QueryClient is not available from @tanstack/react-query. " +
      "This may be a Parcel bundling issue. Try setting scopeHoist: true in package.json",
  );
}

export const retry =
  (number = 1) =>
  (failureCount, error) => {
    if (
      error?.cause?.problem === "CANCEL_ERROR" ||
      error?.cause?.status === 451 // firefly "failed"
    ) {
      return false;
    }

    if (getRetryAfterMs(error?.cause) === null) {
      console.warn(
        `Query retry attempt ${failureCount + 1} due to error:`,
        error,
      );
    }

    // Rate limited requests are retried beyond `number`: they are queued and
    // delayed by `Retry-After`, so they do not add pressure on the server.
    if (error?.cause?.status === 429 || error?.cause?.shouldRetry) {
      return failureCount < MAX_RATE_LIMIT_RETRIES;
    }

    return failureCount < number;
  };

export const retryDelay =
  (number = 30000) =>
  (retryAttempt, error) => {
    // Honour `Retry-After` (header, or the value carried on the error cause)
    // so a retried request does not hit the rate limit again.
    const retryAfterMs = getRetryAfterMs(error?.cause);

    return retryAfterMs === null
      ? delayWithJitter(Math.min(1000 * 2 ** retryAttempt, number))
      : delayWithUpwardJitter(retryAfterMs);
  };

const getApiErrorMessage = (error) => {
  const message = error?.cause?.message ?? error?.cause?.data?.error ?? "";

  return typeof message === "string" ? message.trim() : "";
};

const formatErrorMessage = (baseMessage, error) => {
  const apiMessage = getApiErrorMessage(error);

  return apiMessage ? `${baseMessage} ${apiMessage}` : baseMessage;
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: async (error, query) => {
      console.warn("Query error:", query, error);

      if (query?.meta?.errorMessage) {
        store
          .getState()
          .addToast(formatErrorMessage(query.meta.errorMessage, error), {
            variant: "negative",
            timeout: 1000 * 60,
          });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: async (error, variables, onMutateResult, mutation) => {
      console.warn("Mutation error:", mutation, variables, error);

      if (mutation?.meta?.errorMessage) {
        store
          .getState()
          .addToast(formatErrorMessage(mutation.meta.errorMessage, error), {
            variant: "negative",
            timeout: 1000 * 60,
          });
      }
    },
  }),
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      retry: retry(),
      retryDelay: retryDelay(),
    },
    mutations: {
      retry: retry(0),
      retryDelay: retryDelay(),
    },
  },
});

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

export const asyncStoragePersister = createAsyncStoragePersister({
  storage: { getItem, setItem, removeItem, entries },
  serialize: (data) => data,
  deserialize: (data) => data,
});
